/**
 * PublicReferral — vista SIN AUTH del referido (para especialista externo).
 * URL: /r/:token
 * Muestra el referido en modo lectura + botón "Confirmar recepción".
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, Stethoscope, Printer } from "@/components/icons";
import { doctorLabel } from "../lib/doctorLabel";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-DO", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export default function PublicReferral() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, err: null, data: null });
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/referrals/${token}`)
      .then((r) => {
        setState({ loading: false, err: null, data: r.data });
        setConfirmed(!!r.data.external_confirmed_at);
      })
      .catch((e) => {
        const msg =
          e.response?.status === 410
            ? "Este enlace ha expirado."
            : e.response?.status === 404
              ? "Este referido no existe o el enlace es incorrecto."
              : "No se pudo cargar el referido.";
        setState({ loading: false, err: msg, data: null });
      });
  }, [token]);

  const confirm = async () => {
    setConfirming(true);
    try {
      const r = await axios.post(`${API}/public/referrals/${token}/confirm`);
      setConfirmed(true);
      if (r.data?.already_confirmed) {
        toast.info("Este referido ya había sido confirmado.");
      } else {
        toast.success("Recepción confirmada. El doctor emisor ha sido notificado.");
      }
    } catch {
      toast.error("No se pudo confirmar la recepción.");
    } finally {
      setConfirming(false);
    }
  };

  if (state.loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Cargando referido…
      </div>
    );

  if (state.err)
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl p-10 max-w-md text-center shadow-lg">
          <div className="w-14 h-14 rounded-full bg-red-50 mx-auto flex items-center justify-center mb-4">
            <Stethoscope className="w-6 h-6 text-red-500" />
          </div>
          <h1 className="text-xl font-extrabold text-[#163144]">No disponible</h1>
          <p className="text-slate-500 mt-2 text-sm">{state.err}</p>
        </div>
      </div>
    );

  const r = state.data;
  const doctorTitle = doctorLabel(r.from_doctor_name);
  const specialty = r.from_doctor_specialty || "";
  const target =
    r.external_specialty || "Especialista" +
    (r.external_doctor_name ? ` — ${r.external_doctor_name}` : "");

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="public-referral">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Banner top */}
        <div className="bg-[#01BC8E] text-white rounded-2xl px-6 py-4 mb-6 flex items-center gap-3">
          <Stethoscope className="w-6 h-6" />
          <div>
            <div className="text-xs uppercase tracking-wider opacity-80">
              Referido médico compartido
            </div>
            <div className="text-lg font-extrabold">
              {r.patient_name}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-md px-6 sm:px-10 py-8">
          {/* Membrete */}
          <div className="text-center">
            {r.clinic_logo_url && (
              <img
                src={r.clinic_logo_url}
                alt="Logo"
                className="h-14 mx-auto mb-2 object-contain"
              />
            )}
            {r.clinic_name && (
              <div className="text-sm uppercase tracking-tight font-extrabold text-[#1E4FBF]">
                {r.clinic_name}
              </div>
            )}
            <div className="text-base font-extrabold text-[#163144] mt-1">
              {doctorTitle}
              {specialty ? ` — ${specialty}` : ""}
            </div>
            {r.clinic_phone && (
              <div className="text-xs text-slate-500 mt-1">{r.clinic_phone}</div>
            )}
            {r.exequatur_number && (
              <div className="text-xs text-slate-500">
                Exequátur: {r.exequatur_number}
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 my-6" />

          {/* Cabecera */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-slate-400">
                Fecha
              </div>
              <div className="font-bold text-[#163144]">
                {fmtDate(r.created_at)}
              </div>
            </div>
            {r.urgency === "urgente" && (
              <span className="px-3 py-1 rounded-full text-xs uppercase tracking-wider font-bold bg-red-100 text-red-700">
                Urgente
              </span>
            )}
          </div>

          <div className="bg-slate-50 rounded-2xl p-5 mb-4">
            <div className="text-xs uppercase tracking-wider font-bold text-slate-400">
              Dirigido a
            </div>
            <div className="text-base font-extrabold text-[#163144] mt-1">
              {target}
              {r.external_center ? ` · ${r.external_center}` : ""}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-slate-400">
                Paciente
              </div>
              <div className="text-base font-bold text-[#163144] mt-1">
                {r.patient_name}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-slate-400">
                Emitido por
              </div>
              <div className="text-base font-bold text-[#163144] mt-1">
                {doctorTitle}
              </div>
            </div>
          </div>

          <div className="bg-slate-50 rounded-2xl p-5 text-sm text-[#163144] space-y-3">
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-slate-400">
                Motivo del referido
              </div>
              <div className="mt-1">{r.reason}</div>
            </div>
            {r.clinical_notes && (
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-slate-400">
                  Notas clínicas
                </div>
                <div className="mt-1 whitespace-pre-line">
                  {r.clinical_notes}
                </div>
              </div>
            )}
          </div>

          {/* Firma */}
          {(r.signature_url || r.stamp_url) && (
            <div className="mt-14 flex justify-center">
              <div className="relative text-center">
                {r.stamp_url && (
                  <img
                    src={r.stamp_url}
                    alt="Sello"
                    className="absolute -top-10 left-1/2 -translate-x-1/2 h-24 opacity-70 pointer-events-none"
                  />
                )}
                {r.signature_url && (
                  <img
                    src={r.signature_url}
                    alt="Firma"
                    className="relative h-14 z-10 mx-auto"
                  />
                )}
                <div className="w-64 border-t border-slate-300 mt-1 pt-1.5 text-xs font-bold text-[#163144]">
                  {doctorTitle}
                </div>
              </div>
            </div>
          )}

          {/* Acción: confirmar recepción */}
          <div className="mt-8 border-t border-slate-100 pt-6 flex flex-col items-center">
            {confirmed ? (
              <div
                className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-blue-50 text-[#019A75] font-bold border border-emerald-200"
                data-testid="ref-confirmed-badge"
              >
                <CheckCircle className="w-5 h-5" />
                Recepción confirmada
                {r.external_confirmed_at && (
                  <span className="text-xs font-normal opacity-70 ml-1">
                    · {fmtDate(r.external_confirmed_at)}
                  </span>
                )}
              </div>
            ) : (
              <button
                onClick={confirm}
                disabled={confirming}
                data-testid="ref-confirm-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-[#01BC8E] hover:bg-[#019A75] text-white font-bold shadow-lg shadow-emerald-500/30 disabled:opacity-50"
              >
                <CheckCircle className="w-5 h-5" />
                {confirming ? "Confirmando…" : "Confirmar recepción"}
              </button>
            )}
            <p className="text-xs text-slate-400 mt-3 text-center max-w-xs">
              Al confirmar, el doctor emisor recibirá una notificación con la fecha y hora.
            </p>
            <button
              onClick={() => window.print()}
              className="mt-4 text-xs font-bold text-slate-500 hover:text-[#163144] inline-flex items-center gap-1.5"
            >
              <Printer className="w-3.5 h-3.5" /> Imprimir esta página
            </button>
          </div>
        </div>

        <div className="text-center text-xs text-slate-400 mt-6 flex items-center justify-center gap-2">
          <img
            src="/brand/consudoctor-icon.png"
            alt="ConsuDoctor"
            className="h-5 w-5 object-contain opacity-70"
          />
          <span>
            Enlace válido hasta {fmtDate(r.token_expires_at)} · ConsuDoctor · www.consudoctor.com
          </span>
        </div>
      </div>
    </div>
  );
}
